import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Table, 
  Badge,
  ButtonGroup,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import api from "../api/api";
import swal from "sweetalert";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import { getSensorDepths } from "../utils/sensorDepths";

const FieldHistoryPage = () => {
  const { t } = useTranslation();
  const { fieldId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Get field from navigation state or fetch it
  const [field, setField] = useState(location.state?.field || null);
  const [fieldHistory, setFieldHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSensorFilter, setSelectedSensorFilter] = useState("all");
  const [viewMode, setViewMode] = useState("table"); // table or chart

  // Date range state
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Pagination for table
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  const getDepthLabel = (key, level) => {
    if (selectedSensorFilter === "all") return `${t("niveau")} ${level}`;
    const reading = fieldHistory.find(
      (item) => item.sensor_code === selectedSensorFilter
    );
    return `${getSensorDepths(reading?.sensorDepths)[key]} cm`;
  };

  // Fetch field details if not provided
  useEffect(() => {
    if (!field && fieldId) {
      fetchFieldDetails();
    }
  }, [fieldId, field]);

  // Auto-fetch on mount
  useEffect(() => {
    if (field || fieldId) {
      fetchFieldHistory();
    }
  }, []);

  const fetchFieldDetails = async () => {
    try {
      const response = await api.get(`/admin/fields`);
      const foundField = response.data.fields.find(
        (f) => f.id === parseInt(fieldId)
      );
      if (foundField) {
        setField(foundField);
      } else {
        swal({
          icon: "error",
          title: t('error'),
          text: t('field_not_found'),
        });
        navigate(-1);
      }
    } catch (error) {
      console.error("Error fetching field details:", error);
      swal({
        icon: "error",
        title: t('error'),
        text: t('cannot_load_field_details'),
      });
    }
  };
  // Add state for chart type
  const [chartType, setChartType] = useState("temp-humidity");

  // Prepare data for charts (sample every N points for performance)
  const prepareChartData = (data) => {
    if (data.length === 0) return [];

    // Sample data if too many points (for performance)
    const maxPoints = 200;
    const step = Math.ceil(data.length / maxPoints);

    return data
      .filter((_, index) => index % step === 0)
      .map((reading) => ({
        time: reading.time,
        temperature: reading.temperature || null,
        humidity: reading.humidity || null,
        pressure: reading.pressure || null,
        mv1: reading.mv1 || null,
        mv2: reading.mv2 || null,
        mv3: reading.mv3 || null,
        charge: reading.charge || null,
        sensor: reading.sensor_code,
      }))
      .reverse(); // Reverse to show oldest to newest
  };
  const fetchFieldHistory = async () => {
    if (!fieldId && !field?.id) return;

    setLoading(true);
    try {
      const targetFieldId = fieldId || field.id;
      const response = await api.get(
        `/field/${targetFieldId}/history/${dateRange.start}/${dateRange.end}`,
        {
          params: {
            pageNum: 1,
            limit: 10000,
          },
        }
      );

      if (response.data.type === "success") {
        setFieldHistory(response.data.history || []);

        if (response.data.history.length === 0) {
          const sensorCodes = response.data.sensor_chains
            ?.map((chain) => chain.current.sensor_code)
            .join(", ");

          swal({
            icon: "info",
            title: t('no_data'),
            text: t('no_readings_for_sensors', { 
              sensors: sensorCodes, 
              start: dateRange.start, 
              end: dateRange.end 
            }),
            button: "OK",
          });
        }
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching field history:", error);

      if (error.response?.data?.message === "no_sensors_found_for_field") {
        swal({
          icon: "info",
          title: t('no_sensors'),
          text: t('no_sensors_installed'),
        });
      } else {
        swal({
          icon: "error",
          title: t('error'),
          text: error.response?.data?.message || t('error_fetching_history'),
        });
      }
      setFieldHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const filteredData =
      selectedSensorFilter === "all"
        ? fieldHistory
        : fieldHistory.filter((h) => h.sensor_code === selectedSensorFilter);

    const headers = [
      t('datetime'),
      t('sensor'),
      t('temperature_c'),
      t('humidity_percent_full'),
      t('pressure_hpa_full'),
      `${getDepthLabel("mv1", 1)} (%)`,
      `${getDepthLabel("mv2", 2)} (%)`,
      `${getDepthLabel("mv3", 3)} (%)`,
      t('charge_percent'),
    ];

    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        [
          new Date(row.time).toLocaleString("fr-FR"),
          row.sensor_code,
          row.temperature?.toFixed(2) || "",
          row.humidity?.toFixed(2) || "",
          row.pressure?.toFixed(2) || "",
          row.mv1?.toFixed(2) || "",
          row.mv2?.toFixed(2) || "",
          row.mv3?.toFixed(2) || "",
          row.charge || "",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `historique_${field?.name}_${dateRange.start}_${dateRange.end}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Statistics calculation
  const getStatistics = () => {
    if (fieldHistory.length === 0) {
      return {
        totalReadings: 0,
        uniqueSensors: 0,
        avgTemp: 0,
        avgHumidity: 0,
        avgMV1: 0,
        minCharge: 0,
        maxCharge: 0,
      };
    }

    return {
      totalReadings: fieldHistory.length,
      uniqueSensors: [...new Set(fieldHistory.map((h) => h.sensor_code))]
        .length,
      avgTemp:
        fieldHistory.reduce((sum, r) => sum + (r.temperature || 0), 0) /
        fieldHistory.length,
      avgHumidity:
        fieldHistory.reduce((sum, r) => sum + (r.humidity || 0), 0) /
        fieldHistory.length,
      avgMV1:
        fieldHistory.reduce((sum, r) => sum + (r.mv1 || 0), 0) /
        fieldHistory.length,
      minCharge: Math.min(...fieldHistory.map((r) => r.charge || 100)),
      maxCharge: Math.max(...fieldHistory.map((r) => r.charge || 0)),
    };
  };

  const stats = getStatistics();

  // Pagination
  const filteredHistory =
    selectedSensorFilter === "all"
      ? fieldHistory
      : fieldHistory.filter((h) => h.sensor_code === selectedSensorFilter);

  const paginatedData = filteredHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  return (
    <Container fluid className="main-content-container px-4">
      {/* Page Header */}
      <Row noGutters className="page-header  py-4">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-3"
          >
            <i className="material-icons align-middle" aria-hidden="true">&#xe5c4;</i> {t('back')}
          </Button>
          <PageTitle
            title={`${t('history_readings')} - ${field?.name || t('field')}`}
            subtitle={`${t('farm')}: ${field?.farm?.name || "-"} | `}
            className="text-sm-left"
          />
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row className="mb-4 gap-1 d-flex justify-content-around">
        <Col lg="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="text-center py-3">
              <i
                className="material-icons text-primary mb-2"
                style={{ fontSize: "32px" }} aria-hidden="true">
                &#xe1b8;
              </i>
              <div className="small text-muted">{t('readings')}</div>
              <h4 className="mb-0 fw-bold text-primary">
                {stats.totalReadings}
              </h4>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="text-center py-3">
              <i
                className="material-icons text-success mb-2"
                style={{ fontSize: "32px" }} aria-hidden="true">
                &#xe8b5;
              </i>
              <div className="small text-muted">{t('sensors')}</div>
              <h4 className="mb-0 fw-bold text-success">
                {stats.uniqueSensors}
              </h4>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="text-center py-3">
              <i
                className="material-icons text-warning mb-2"
                style={{ fontSize: "32px" }} aria-hidden="true">
                &#xe3a6;
              </i>
              <div className="small text-muted">{t('avg_temp')}</div>
              <h4 className="mb-0 fw-bold text-warning">
                {stats.avgTemp.toFixed(1)}°C
              </h4>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="text-center py-3">
              <i
                className="material-icons text-info mb-2"
                style={{ fontSize: "32px" }} aria-hidden="true">
                &#xe3a7;
              </i>
              <div className="small text-muted">{t('avg_humidity')}</div>
              <h4 className="mb-0 fw-bold text-info">
                {stats.avgHumidity.toFixed(1)}%
              </h4>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="text-center py-3">
              <i
                className="material-icons text-danger mb-2"
                style={{ fontSize: "32px" }} aria-hidden="true">
                &#xe1a3;
              </i>
              <div className="small text-muted">{t('battery')}</div>
              <h5
                className="mb-0 fw-bold text-danger"
                style={{ fontSize: "1.1rem" }}
              >
                {stats.minCharge}% - {stats.maxCharge}%
              </h5>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters & Controls */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3 gap-3">
            {/* Date Range */}
            <Col lg="3" md="6">
              <Form.Group>
                <Form.Label className="small fw-bold mb-1">
                  <i
                    className="material-icons align-middle me-1"
                    style={{ fontSize: "16px" }} aria-hidden="true">
                    &#xe8df;
                  </i>
                  {t('date_start')}
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateRange.start}
                  max={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                />
              </Form.Group>
            </Col>

            <Col lg="3" md="6">
              <Form.Group>
                <Form.Label className="small fw-bold mb-1">
                  <i
                    className="material-icons align-middle me-1"
                    style={{ fontSize: "16px" }} aria-hidden="true">
                    &#xe8df;
                  </i>
                  {t('date_end')}
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateRange.end}
                  min={dateRange.start}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                />
              </Form.Group>
            </Col>

            <Col></Col>
            {/* Sensor Filter */}
            <Col lg="3" md="6">
              <Form.Group>
                <Form.Label className="small fw-bold mb-1">
                  <i
                    className="material-icons align-middle me-1"
                    style={{ fontSize: "16px" }} aria-hidden="true">
                    &#xe8b5;
                  </i>
                  {t('sensor')}
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={selectedSensorFilter}
                  onChange={(e) => {
                    setSelectedSensorFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">{t('all_sensors')}</option>
                  {[...new Set(fieldHistory.map((h) => h.sensor_code))].map(
                    (code) => (
                      <option key={code} value={code}>
                        {code} (
                        {
                          fieldHistory.filter((h) => h.sensor_code === code)
                            .length
                        }
                        )
                      </option>
                    )
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row className="mt-3">
            <Col className="d-flex justify-content-between align-items-center">
              <Button
                variant="primary"
                size="sm"
                onClick={fetchFieldHistory}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    {t('loading')}
                  </>
                ) : (
                  <>
                    <i className="material-icons align-middle me-1" aria-hidden="true">&#xe8b6;</i>
                    {t('search')}
                  </>
                )}
              </Button>

              <div className="d-flex gap-2">
                <ButtonGroup size="sm">
                  <Button
                    variant={
                      viewMode === "table" ? "primary" : "outline-primary"
                    }
                    aria-pressed={viewMode === "table"}
                    onClick={() => setViewMode("table")}
                  >
                    <i className="material-icons align-middle" aria-hidden="true">&#xe8ef;</i>{" "}
                    {t('table')}
                  </Button>
                  <Button
                    variant={
                      viewMode === "chart" ? "primary" : "outline-primary"
                    }
                    aria-pressed={viewMode === "chart"}
                    onClick={() => setViewMode("chart")}
                  >
                    <i className="material-icons align-middle" aria-hidden="true">&#xe6e1;</i>{" "}
                    {t('charts')}
                  </Button>
                </ButtonGroup>

                <Button
                  variant="success"
                  size="sm"
                  onClick={exportToCSV}
                  disabled={fieldHistory.length === 0}
                >
                  <i className="material-icons align-middle me-1" aria-hidden="true">&#xe2c4;</i>
                  {t('export_csv')}
                </Button>
              </div>
            </Col>
          </Row>

          {/* Active Filters Display */}
          {selectedSensorFilter !== "all" && (
            <Row className="mt-3">
              <Col>
                <Alert
                  variant="info"
                  className="py-2 mb-0 d-flex align-items-center justify-content-between"
                >
                  <span>
                    <i className="material-icons align-middle me-1" aria-hidden="true">&#xe8b5;</i>
                    {t('filtered_by_sensor')}: <strong>{selectedSensorFilter}</strong>
                  </span>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-decoration-none p-0"
                    onClick={() => setSelectedSensorFilter("all")}
                  >
                    {t('reset')}
                  </Button>
                </Alert>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Sensors List */}
      {[...new Set(fieldHistory.map((h) => h.sensor_code))].length > 1 && (
        <Card className="mb-3 border-0 shadow-sm">
          <Card.Body className="py-2">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <small className="text-muted fw-bold">{t('active_sensors')}:</small>
              {[...new Set(fieldHistory.map((h) => h.sensor_code))].map(
                (code) => (
                  <Badge
                    key={code}
                    bg={selectedSensorFilter === code ? "primary" : "secondary"}
                    className="px-3 py-2"
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedSensorFilter === code}
                    onClick={() =>
                      setSelectedSensorFilter(
                        selectedSensorFilter === code ? "all" : code
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedSensorFilter(
                          selectedSensorFilter === code ? "all" : code
                        );
                      }
                    }}
                  >
                    {code} (
                    {fieldHistory.filter((h) => h.sensor_code === code).length})
                  </Badge>
                )
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Results Info */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted">
          {t('displaying')} <strong>{filteredHistory.length}</strong> {t('readings_count')}
          {totalPages > 1 && (
            <>
              {" "}
              | {t('page')} {currentPage} / {totalPages}
            </>
          )}
        </span>
        {fieldHistory.length > 0 && (
          <small className="text-muted">
            <i
              className="material-icons align-middle"
              style={{ fontSize: "14px" }} aria-hidden="true">
              &#xe88e;
            </i>{" "}
            {t('last_reading')}:{" "}
            {new Date(fieldHistory[0]?.time).toLocaleString("fr-FR")}
          </small>
        )}
      </div>

      {/* Main Content */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner
                animation="border"
                variant="primary"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-muted mt-3">{t('retrieving_data')}</p>
            </div>
          ) : fieldHistory.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="material-icons text-muted mb-3"
                style={{ fontSize: "64px" }} aria-hidden="true">
                &#xe002;
              </i>
              <h5 className="text-muted">{t('no_data_available')}</h5>
              <p className="text-muted">
                {t('no_readings_found')}
              </p>
              <Button variant="primary" onClick={fetchFieldHistory}>
                <i className="material-icons align-middle me-1" aria-hidden="true">&#xe5d5;</i>
                {t('retry')}
              </Button>
            </div>
          ) : viewMode === "table" ? (
            <>
              {/* Table View */}
              <div
                className="table-responsive"
                style={{ maxHeight: "600px", overflowY: "auto" }}
              >
                <Table hover size="sm" className="mb-0">
                  <thead
                    className="sticky-top bg-light shadow-sm"
                    style={{ zIndex: 1 }}
                  >
                    <tr>
                      <th scope="col">{t('datetime')}</th>
                      <th scope="col">{t('sensor')}</th>
                      <th scope="col" className="text-end">{t('temp_c')}</th>
                      <th scope="col" className="text-end">{t('humidity_percent')}</th>
                      <th scope="col" className="text-end">{t('pressure_hpa')}</th>
                      <th scope="col" className="text-center">{getDepthLabel("mv1", 1)} (%)</th>
                      <th scope="col" className="text-center">{getDepthLabel("mv2", 2)} (%)</th>
                      <th scope="col" className="text-center">{getDepthLabel("mv3", 3)} (%)</th>
                      <th scope="col" className="text-center">{t('charge')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((reading, idx) => (
                      <tr key={`${reading.sensor_id}-${reading.time}-${idx}`}>
                        <td
                          style={{ whiteSpace: "nowrap", fontSize: "0.85rem" }}
                        >
                          {new Date(reading.time).toLocaleString("fr-FR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td>
                          <Badge
                            bg="dark"
                            className="font-monospace"
                            style={{ fontSize: "0.75rem" }}
                          >
                            {reading.sensor_code}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <span className="badge bg-light text-dark border">
                            {reading.temperature?.toFixed(1) || "-"}°
                          </span>
                        </td>
                        <td className="text-end">
                          <span className="badge bg-light text-dark border">
                            {reading.humidity?.toFixed(1) || "-"}%
                          </span>
                        </td>
                        <td className="text-end">
                          <span className="badge bg-light text-dark border">
                            {reading.pressure?.toFixed(1) || "-"}
                          </span>
                        </td>
                        <td className="text-center">
                          <Badge
                            bg={
                              reading.mv1 > 70
                                ? "success"
                                : reading.mv1 > 40
                                ? "warning"
                                : reading.mv1 > 0
                                ? "danger"
                                : "secondary"
                            }
                            className="px-2"
                            style={{ minWidth: "55px" }}
                          >
                            {reading.mv1 > 0
                              ? `${reading.mv1.toFixed(1)}%`
                              : "-"}
                          </Badge>
                          {selectedSensorFilter === "all" && (
                            <small className="d-block text-muted">
                              {getSensorDepths(reading.sensorDepths).mv1} cm
                            </small>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge
                            bg={
                              reading.mv2 > 70
                                ? "success"
                                : reading.mv2 > 40
                                ? "warning"
                                : reading.mv2 > 0
                                ? "danger"
                                : "secondary"
                            }
                            className="px-2"
                            style={{ minWidth: "55px" }}
                          >
                            {reading.mv2 > 0
                              ? `${reading.mv2.toFixed(1)}%`
                              : "-"}
                          </Badge>
                          {selectedSensorFilter === "all" && (
                            <small className="d-block text-muted">
                              {getSensorDepths(reading.sensorDepths).mv2} cm
                            </small>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge
                            bg={
                              reading.mv3 > 70
                                ? "success"
                                : reading.mv3 > 40
                                ? "warning"
                                : reading.mv3 > 0
                                ? "danger"
                                : "secondary"
                            }
                            className="px-2"
                            style={{ minWidth: "55px" }}
                          >
                            {reading.mv3 > 0
                              ? `${reading.mv3.toFixed(1)}%`
                              : "-"}
                          </Badge>
                          {selectedSensorFilter === "all" && (
                            <small className="d-block text-muted">
                              {getSensorDepths(reading.sensorDepths).mv3} cm
                            </small>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge
                            bg={
                              reading.charge > 80
                                ? "success"
                                : reading.charge > 50
                                ? "warning"
                                : "danger"
                            }
                            className="px-2"
                            style={{ minWidth: "55px" }}
                          >
                            <i
                              className="material-icons"
                              style={{
                                fontSize: "12px",
                                verticalAlign: "middle",
                              }} aria-hidden="true">
                            {reading.charge > 80
    ? "battery_full"
    : reading.charge > 50
    ? "battery_std"
    : "battery_alert"}
                            </i>{" "}
                            {reading.charge}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <small className="text-muted">
                    {t('showing')} {(currentPage - 1) * itemsPerPage + 1} -{" "}
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredHistory.length
                    )}{" "}
                    {t('of')} {filteredHistory.length}
                  </small>
                  <ButtonGroup size="sm">
                    <Button
                      variant="outline-primary"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    >
                      <i className="material-icons" aria-hidden="true">&#xe5dc;</i>
                    </Button>
                    <Button
                      variant="outline-primary"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <i className="material-icons" aria-hidden="true">&#xe5cb;</i>
                    </Button>
                    <Button variant="outline-secondary" disabled>
                      {currentPage} / {totalPages}
                    </Button>
                    <Button
                      variant="outline-primary"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <i className="material-icons" aria-hidden="true">&#xe5cc;</i>
                    </Button>
                    <Button
                      variant="outline-primary"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      <i className="material-icons" aria-hidden="true">&#xe5dd;</i>
                    </Button>
                  </ButtonGroup>
                </div>
              )}
            </>
          ) : (
            /* Chart View */
            <div className="p-3">
              {/* Chart Type Selector */}
              <div className="mb-4 d-flex justify-content-center gap-2">
                <Button
                  variant={
                    chartType === "temp-humidity"
                      ? "primary"
                      : "outline-primary"
                  }
                  size="sm"
                  aria-pressed={chartType === "temp-humidity"}
                  onClick={() => setChartType("temp-humidity")}
                >
                  <i className="material-icons align-middle" aria-hidden="true">&#xe3a6;</i>{" "}
                  {t('temp_humidity')}
                </Button>
                <Button
                  variant={
                    chartType === "soil-moisture"
                      ? "success"
                      : "outline-success"
                  }
                  size="sm"
                  aria-pressed={chartType === "soil-moisture"}
                  onClick={() => setChartType("soil-moisture")}
                >
                  <i className="material-icons align-middle" aria-hidden="true">&#xe3aa;</i>{" "}
                  {t('soil_moisture')}
                </Button>
                <Button
                  variant={
                    chartType === "battery" ? "warning" : "outline-warning"
                  }
                  size="sm"
                  aria-pressed={chartType === "battery"}
                  onClick={() => setChartType("battery")}
                >
                  <i className="material-icons align-middle" aria-hidden="true">&#xe1a3;</i>{" "}
                  {t('battery_charge')}
                </Button>
                <Button
                  variant={chartType === "pressure" ? "info" : "outline-info"}
                  size="sm"
                  aria-pressed={chartType === "pressure"}
                  onClick={() => setChartType("pressure")}
                >
                  <i className="material-icons align-middle" aria-hidden="true">&#xe3a7;</i>{" "}
                  {t('pressure')}
                </Button>
              </div>

              {/* Chart Container */}
              <ResponsiveContainer width="100%" height={400}>
                {chartType === "temp-humidity" && (
                  <LineChart data={prepareChartData(filteredHistory)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tickFormatter={(time) =>
                        new Date(time).toLocaleDateString("fr-FR", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                        })
                      }
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      yAxisId="left"
                      label={{
                        value: t('temperature_c'),
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      label={{
                        value: t('humidity_percent_full'),
                        angle: 90,
                        position: "insideRight",
                      }}
                    />
                    <Tooltip
                      labelFormatter={(time) =>
                        new Date(time).toLocaleString("fr-FR")
                      }
                      formatter={(value) => [Number(value).toFixed(2), ""]}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="temperature"
                      stroke="#ff9800"
                      name={t('temperature_c')}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="humidity"
                      stroke="#2196f3"
                      name={t('humidity_percent_full')}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                )}

                {chartType === "soil-moisture" && (
                  <LineChart data={prepareChartData(filteredHistory)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tickFormatter={(time) =>
                        new Date(time).toLocaleDateString("fr-FR", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                        })
                      }
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      label={{
                        value: t('soil_moisture_percent'),
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      labelFormatter={(time) =>
                        new Date(time).toLocaleString("fr-FR")
                      }
                      formatter={(value) => [Number(value).toFixed(2), ""]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="mv1"
                      stroke="#4caf50"
                      name={`${getDepthLabel("mv1", 1)} (%)`}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="mv2"
                      stroke="#8bc34a"
                      name={`${getDepthLabel("mv2", 2)} (%)`}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="mv3"
                      stroke="#cddc39"
                      name={`${getDepthLabel("mv3", 3)} (%)`}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                )}

                {chartType === "battery" && (
                  <LineChart data={prepareChartData(filteredHistory)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tickFormatter={(time) =>
                        new Date(time).toLocaleDateString("fr-FR", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                        })
                      }
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      domain={[0, 100]}
                      label={{
                        value: t('charge_percent'),
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      labelFormatter={(time) =>
                        new Date(time).toLocaleString("fr-FR")
                      }
                      formatter={(value) => [
                        Number(value).toFixed(0) + "%",
                        "",
                      ]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="charge"
                      stroke="#f44336"
                      name={t('battery_charge')}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                )}

                {chartType === "pressure" && (
                  <LineChart data={prepareChartData(filteredHistory)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      tickFormatter={(time) =>
                        new Date(time).toLocaleDateString("fr-FR", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                        })
                      }
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      label={{
                        value: t('pressure_hpa_full'),
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      labelFormatter={(time) =>
                        new Date(time).toLocaleString("fr-FR")
                      }
                      formatter={(value) => [Number(value).toFixed(2), ""]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="pressure"
                      stroke="#9c27b0"
                      name={t('pressure_hpa_full')}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>

              {/* Chart Info */}
              <div className="mt-3 text-center">
                <small className="text-muted">
                  <i
                    className="material-icons align-middle"
                    style={{ fontSize: "14px" }} aria-hidden="true">
                    &#xe88e;
                  </i>{" "}
                  {filteredHistory.length} {t('data_points_displayed')}
                </small>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default FieldHistoryPage;
